import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ArrowLeft, BookOpen, Save, Plus, Heart } from 'lucide-react';
import { toast } from 'sonner';
import EbookPageFooter from '../shared/EbookPageFooter';

export default function GratitudeExercises() {
  const navigate = useNavigate();
  const [exercise1Entries, setExercise1Entries] = useState(() => {
    const saved = localStorage.getItem('gratitudeExercise1');
    return saved ? JSON.parse(saved) : [{ action: '', feeling: '', need: '' }];
  });
  const [exercise2Entries, setExercise2Entries] = useState(() => {
    const saved = localStorage.getItem('gratitudeExercise2');
    return saved ? JSON.parse(saved) : [{ action: '', feeling: '', need: '', expressed: false }];
  });

  const saveProgress = () => {
    localStorage.setItem('gratitudeExercise1', JSON.stringify(exercise1Entries));
    localStorage.setItem('gratitudeExercise2', JSON.stringify(exercise2Entries));
    toast.success('Gratitude journal saved!');
  };

  const addEntry = (exercise) => {
    if (exercise === 1) setExercise1Entries([...exercise1Entries, { action: '', feeling: '', need: '' }]);
    else setExercise2Entries([...exercise2Entries, { action: '', feeling: '', need: '', expressed: false }]);
  };

  const updateEntry = (exercise, idx, field, value) => {
    if (exercise === 1) {
      const updated = [...exercise1Entries];
      updated[idx][field] = value;
      setExercise1Entries(updated);
    } else {
      const updated = [...exercise2Entries];
      updated[idx][field] = value;
      setExercise2Entries(updated);
    }
  };

  useEffect(() => {
    const interval = setInterval(saveProgress, 30000);
    return () => clearInterval(interval);
  }, [exercise1Entries, exercise2Entries]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Gratitude Exercises</h1>
                <p className="text-slate-300 text-sm">Daily Gratitude Journal</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={saveProgress} variant="outline" size="sm" className="border-green-600 hover:bg-green-900/20 text-white">
                <Save className="h-4 w-4 mr-2" />Save
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm"><BookOpen className="h-4 w-4" /><span>Page 38</span></div>
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <Heart className="h-16 w-16 text-rose-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">Daily Gratitude Journal</h2>
          </div>

          {/* Exercise 1 */}
          <div className="mb-12">
            <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-6 mb-6">
              <h3 className="text-2xl font-bold text-blue-200 mb-4">EXERCISE 1: Things I Did to Enrich My Life</h3>
              <p className="text-blue-100 leading-relaxed mb-3">
                Each night before going to sleep (or as often as you desire) write down one thing that you did that enriched your life or someone else's life. 
                Record the specific action you did, the feeling that you had, and need that was met.
              </p>
              <div className="bg-blue-900/30 rounded p-4">
                <p className="text-blue-200 font-semibold mb-2">Examples:</p>
                <p className="text-blue-100 text-sm mb-2">• When I took a break from my computer work this afternoon to go for a hike, I felt enlivened and happy because I met my needs for self care and exercise.</p>
                <p className="text-blue-100 text-sm">• When I bought the newest book from my favorite author, I felt excited because it met my need for learning and recreation.</p>
              </div>
              <p className="text-blue-200 mt-4 font-semibold">Then SAVOR it. Spend 30-60 seconds feeling the good feelings in your body.</p>
            </div>

            {exercise1Entries.map((entry, idx) => (
              <div key={idx} className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-6 mb-4">
                <h4 className="text-white font-semibold mb-4">Entry #{idx + 1}</h4>
                <div className="space-y-4">
                  <div>
                    <label className="text-slate-300 text-sm mb-2 block">When I...</label>
                    <Textarea value={entry.action} onChange={(e) => updateEntry(1, idx, 'action', e.target.value)} placeholder="Describe the specific action..." className="bg-slate-900/40 border-slate-700 text-white placeholder:text-slate-500 min-h-[80px]" />
                  </div>
                  <div>
                    <label className="text-red-300 text-sm mb-2 block">I felt...</label>
                    <Textarea value={entry.feeling} onChange={(e) => updateEntry(1, idx, 'feeling', e.target.value)} placeholder="Your feelings..." className="bg-red-900/20 border-red-700/30 text-white placeholder:text-red-300/40 min-h-[60px]" />
                  </div>
                  <div>
                    <label className="text-green-300 text-sm mb-2 block">Because it met my need for...</label>
                    <Textarea value={entry.need} onChange={(e) => updateEntry(1, idx, 'need', e.target.value)} placeholder="Your needs..." className="bg-green-900/20 border-green-700/30 text-white placeholder:text-green-300/40 min-h-[60px]" />
                  </div>
                  <div className="text-center">
                    <p className="text-amber-300 text-sm">🌼 Give yourself a flower (or gift of appreciation)</p>
                  </div>
                </div>
              </div>
            ))}
            <Button onClick={() => addEntry(1)} variant="outline" className="w-full border-blue-600 text-blue-200 hover:bg-blue-900/20">
              <Plus className="h-4 w-4 mr-2" />Add Another Entry
            </Button>
          </div>

          {/* Exercise 2 */}
          <div>
            <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-6 mb-6">
              <h3 className="text-2xl font-bold text-purple-200 mb-4">EXERCISE 2: Things Others Did to Enrich My Life</h3>
              <p className="text-purple-100 leading-relaxed mb-3">
                Think of something someone else did to enrich your life. Write down the specific action, the feeling you had, and the needs that were met.
              </p>
              <div className="bg-purple-900/30 rounded p-4">
                <p className="text-purple-200 font-semibold mb-2">Examples:</p>
                <p className="text-purple-100 text-sm mb-2">• When my coworkers organized a birthday party for me, I felt warm and tender, because it met my need for celebration and to be seen.</p>
                <p className="text-purple-100 text-sm">• When my partner took time off from work to go to my family reunion, I felt relieved and appreciative because it met my need for companionship and support.</p>
              </div>
              <p className="text-purple-200 mt-4 font-semibold">Then SAVOR it. Recall if you expressed your appreciation to them.</p>
            </div>

            {exercise2Entries.map((entry, idx) => (
              <div key={idx} className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-6 mb-4">
                <h4 className="text-white font-semibold mb-4">Entry #{idx + 1}</h4>
                <div className="space-y-4">
                  <div>
                    <label className="text-slate-300 text-sm mb-2 block">When they...</label>
                    <Textarea value={entry.action} onChange={(e) => updateEntry(2, idx, 'action', e.target.value)} placeholder="What they did..." className="bg-slate-900/40 border-slate-700 text-white placeholder:text-slate-500 min-h-[80px]" />
                  </div>
                  <div>
                    <label className="text-red-300 text-sm mb-2 block">I felt...</label>
                    <Textarea value={entry.feeling} onChange={(e) => updateEntry(2, idx, 'feeling', e.target.value)} placeholder="Your feelings..." className="bg-red-900/20 border-red-700/30 text-white placeholder:text-red-300/40 min-h-[60px]" />
                  </div>
                  <div>
                    <label className="text-green-300 text-sm mb-2 block">Because it met my need for...</label>
                    <Textarea value={entry.need} onChange={(e) => updateEntry(2, idx, 'need', e.target.value)} placeholder="Your needs..." className="bg-green-900/20 border-green-700/30 text-white placeholder:text-green-300/40 min-h-[60px]" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={entry.expressed} onCheckedChange={(checked) => updateEntry(2, idx, 'expressed', checked)} />
                    <label className="text-slate-300 text-sm">I expressed my appreciation to them</label>
                  </div>
                </div>
              </div>
            ))}
            <Button onClick={() => addEntry(2)} variant="outline" className="w-full border-purple-600 text-purple-200 hover:bg-purple-900/20">
              <Plus className="h-4 w-4 mr-2" />Add Another Entry
            </Button>
          </div>

          <p className="text-center text-slate-400 text-sm mt-8">💾 Auto-saves every 30 seconds</p>

          {/* Practice Scenario & Contact Form */}
          <EbookPageFooter 
            pageContext="Gratitude Exercises"
            pageNumber={38}
            showScenario={true}
            showContactForm={true}
          />
        </div>
      </div>
    </div>
  );
}
